import { ErrorMessage } from '@hookform/error-message';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  MenuItem,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { ControllerProps } from 'react-hook-form/dist/types';
import { useTranslation } from 'react-i18next';

import { OrganizationUnit } from '~/data/orgunits/types';
import {
  WorkflowConfig,
  WorkflowStepConfig,
  useWorkflows,
} from '~/data/workflows';
import { OrgUnitLabel } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/OrgUnitLabel';
import { WorkflowApprovalSteps } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigDialog/components/WorkflowApprovalSteps';
import { WorkflowStepField } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigDialog/components/WorkflowStepField';
import { WorkflowStepFieldProps } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigDialog/components/WorkflowStepField/component';
import { useWorkflowConfigForm } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigDialog/useWorkflowConfigForm';
import {
  WorkflowRoles,
  getWorkflowProcessTypeTranslationKeyFromString,
  getWorkflowRolesTranslationKeyFromString,
} from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/data';
import { theme } from '~/theme';

export interface WorkflowConfigFormValues {
  initiators: {
    roleNames: string[];
  };
  approvalSteps: {
    roleNames: string[];
    seq: number;
  }[];
  closer: {
    roleNames: string[];
  };
  overseer: {
    roleNames: string[];
  };
  administrator: {
    roleNames: string[];
  };
}

type PropsType = {
  open: boolean;
  onClose: () => void;
  activeUnitId: number;
  workflowConfig: WorkflowConfig;
  orgUnit: OrganizationUnit | undefined;
  editMode: boolean;
  unitConfigsEnabled: boolean;
};
export const WorkflowConfigDialog = (props: PropsType) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    open,
    onClose,
    workflowConfig,
    orgUnit,
    editMode,
    activeUnitId,
    unitConfigsEnabled,
  } = props;

  const { addUnitWorkflowConfig, updateUnitWorkflowConfig } = useWorkflows();
  const { form } = useWorkflowConfigForm({
    workflowConfig: workflowConfig,
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const handleFormSubmit = useMemo(() => {
    return handleSubmit(
      (formData) => {
        const formOutput: WorkflowConfig = {
          ...workflowConfig,
          unitId: activeUnitId,
          workflow: formData,
        };

        setIsSubmitting(true);
        (editMode
          ? updateUnitWorkflowConfig(formOutput)
          : addUnitWorkflowConfig(formOutput)
        )
          .then(() => {
            enqueueSnackbar(
              editMode ? t('workflowUpdated') : t('workflowCreated'),
              {
                variant: 'success',
              },
            );
            onClose();
          })
          .catch((err) => {
            enqueueSnackbar(err, {
              variant: 'error',
            });
          })
          .finally(() => {
            setIsSubmitting(false);
          });
      },
      (errors) => {
        // TODO: Create better & generic form validation error alert/dialog/popup thing
        // console.log(errors, 'handleSubmit errors');
        Object.values(errors).forEach((fieldError) => {
          enqueueSnackbar(fieldError.message, {
            variant: 'error',
          });
        });
      },
    );
  }, [
    t,
    editMode,
    handleSubmit,
    enqueueSnackbar,
    onClose,
    workflowConfig,
    activeUnitId,
    addUnitWorkflowConfig,
    updateUnitWorkflowConfig,
    setIsSubmitting,
  ]);

  const translateProcessType = (processType: string) =>
    t(getWorkflowProcessTypeTranslationKeyFromString(processType));

  const translateRoleName = (roleName: string) =>
    t(getWorkflowRolesTranslationKeyFromString(roleName));

  const ControlledStepField = (
    fieldProps: Omit<ControllerProps<WorkflowConfigFormValues>, 'render'> &
      Omit<
        WorkflowStepFieldProps,
        'stepConfig' | 'onChange' | 'fieldName' | 'formErrors'
      >,
  ) => {
    const {
      roleOptions,
      label,
      description,
      disabled,
      max,
      lockedOptions,
      renderAddMenuItem,
      ...controllerProps
    } = fieldProps;
    const stepFieldProps = {
      label,
      description,
      disabled,
      roleOptions,
      lockedOptions,
      max,
      renderAddMenuItem,
    };
    return (
      <Controller
        {...controllerProps}
        render={({ field: { value, onChange, name }, formState }) => {
          return (
            <WorkflowStepField
              {...stepFieldProps}
              stepConfig={value as WorkflowStepConfig}
              onChange={onChange}
              fieldName={name}
              formErrors={formState.errors}
            />
          );
        }}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {t('laWorkFlow')}: {translateProcessType(workflowConfig.processType)}
        {!!orgUnit && unitConfigsEnabled && (
          <>
            {' - '}
            <OrgUnitLabel orgUnit={orgUnit} variant={'inline'} />
          </>
        )}
      </DialogTitle>
      <Box
        sx={{ overflow: 'auto', minWidth: '450px', padding: theme.spacing(2) }}
      >
        <Typography variant={'subtitle2'}>{t('workflowProcess')}</Typography>
        <Box>
          <ControlledStepField
            control={control}
            name={'initiators'}
            label={t('workflowInitiator')}
            description={t('workflowInitiatorDescription')}
            roleOptions={[
              WorkflowRoles.EMPLOYEE,
              WorkflowRoles.MANAGER,
              WorkflowRoles.FIRST_APPROVER,
            ]}
            renderAddMenuItem={({
              option,
              onSelect,
              isSelected,
              selection,
            }) => {
              const isEmptySelection = selection.length === 0;

              const canSelectOption = (opt: string) => {
                switch (opt) {
                  case WorkflowRoles.EMPLOYEE:
                  case WorkflowRoles.FIRST_APPROVER:
                    return !selection.includes(WorkflowRoles.MANAGER);
                  case WorkflowRoles.MANAGER:
                    return isEmptySelection;
                  default:
                    return true;
                }
              };

              return (
                <MenuItem
                  key={option}
                  onClick={() => onSelect(option)}
                  disabled={isSelected || !canSelectOption(option)}
                >
                  {translateRoleName(option)}
                </MenuItem>
              );
            }}
          />
        </Box>
        <WorkflowApprovalSteps control={control} />
        <Box>
          <ControlledStepField
            control={control}
            name={'closer'}
            label={t('workflowCloser')}
            description={t('workflowCloserDescription')}
            roleOptions={[]}
            disabled={true}
          />
        </Box>
        <Box>
          <Typography variant={'subtitle2'}>
            {t('workflowSupervisoryRoles')}
          </Typography>

          <ControlledStepField
            control={control}
            name={'overseer'}
            label={t('workflowOverseer')}
            description={t('workflowOverseerDescription')}
            roleOptions={[WorkflowRoles.FINAL_APPROVER]}
          />
          <ControlledStepField
            control={control}
            name={'administrator'}
            label={t('workflowAdministrator')}
            description={t('workflowAdministratorDescription')}
            roleOptions={[WorkflowRoles.FINAL_APPROVER, WorkflowRoles.HR_ADMIN]}
            lockedOptions={[WorkflowRoles.HR_ADMIN]}
          />
        </Box>

        {Object.keys(errors).map((name, index) => {
          return (
            <ErrorMessage
              key={`error-${name}-${index}`}
              errors={errors}
              name={name}
              render={({ message }) => (
                <Typography
                  variant={'caption'}
                  sx={{ color: theme.palette.error.main }}
                  component={'p'}
                >
                  {message}
                </Typography>
              )}
            />
          );
        })}
      </Box>
      <DialogActions>
        <Button onClick={onClose}>{t('close')}</Button>
        <Button onClick={handleFormSubmit} disabled={isSubmitting}>
          {editMode ? t('save') : t('create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
